import React, { useState } from 'react';
import './Settings.css';
import { API_URL } from '../config';

function Settings({ isOpen, onClose, sessionId, onResetSurvey }) {
  const [showConfirmReset, setShowConfirmReset] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [customTitle, setCustomTitle] = useState('Engineering Maturity Assessment');
  const [titleSaveStatus, setTitleSaveStatus] = useState('');
  const [userSessions, setUserSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [anthropicApiKey, setAnthropicApiKey] = useState('');
  const [maskedApiKey, setMaskedApiKey] = useState('');
  const [apiKeyConfigured, setApiKeyConfigured] = useState(false);
  const [apiKeySaveStatus, setApiKeySaveStatus] = useState('');
  const [promptTemplate, setPromptTemplate] = useState('');
  const [promptSaveStatus, setPromptSaveStatus] = useState('');

  React.useEffect(() => {
    const savedTitle = localStorage.getItem('appCustomTitle');
    if (savedTitle) {
      setCustomTitle(savedTitle);
    }
  }, []);

  React.useEffect(() => {
    if (isOpen) {
      fetchAllUserSessions();
      fetchApiKeyStatus();
      fetchPromptTemplate();
    }
  }, [isOpen]);

  const fetchApiKeyStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/anthropic-key`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setApiKeyConfigured(data.configured);
        setMaskedApiKey(data.maskedKey || '');
      }
    } catch (error) {
      console.error('Error fetching API key status:', error);
    }
  };

  const fetchPromptTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/prompt-template`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPromptTemplate(data.promptTemplate || '');
      }
    } catch (error) {
      console.error('Error fetching prompt template:', error);
    }
  };

  const handleSavePromptTemplate = async () => {
    setPromptSaveStatus('saving');
    try {
      const response = await fetch(`${API_URL}/api/admin/prompt-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ promptTemplate })
      });

      if (response.ok) {
        setPromptSaveStatus('success');
        setTimeout(() => setPromptSaveStatus(''), 3000);
      } else {
        setPromptSaveStatus('error');
      }
    } catch (error) {
      console.error('Error saving prompt template:', error);
      setPromptSaveStatus('error');
    }
  };

  const fetchAllUserSessions = async () => {
    setIsLoadingSessions(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/all-sessions`, {
        credentials: 'include'
      });
      
      console.log('Sessions API response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Sessions data received:', data);
        console.log('Number of sessions:', data.sessions ? data.sessions.length : 0);
        setUserSessions(data.sessions || []);
      } else {
        const errorData = await response.json();
        console.error('Failed to fetch user sessions:', response.status, errorData);
      }
    } catch (error) {
      console.error('Error fetching user sessions:', error);
    } finally {
      setIsLoadingSessions(false);
    }
  };

  const handleSaveTitle = () => {
    localStorage.setItem('appCustomTitle', customTitle);
    setTitleSaveStatus('✓ Title saved successfully!');
    setTimeout(() => {
      setTitleSaveStatus('');
      window.location.reload();
    }, 1500);
  };

  const handleSaveApiKey = async () => {
    if (!anthropicApiKey.trim()) {
      setApiKeySaveStatus('Please enter a valid API key');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/anthropic-key`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ apiKey: anthropicApiKey })
      });

      const data = await response.json();

      if (response.ok) {
        setApiKeySaveStatus('✓ API key saved successfully!');
        setAnthropicApiKey('');
        fetchApiKeyStatus();
        setTimeout(() => {
          setApiKeySaveStatus('');
        }, 3000);
      } else {
        setApiKeySaveStatus(`Error: ${data.error}`);
      }
    } catch (error) {
      setApiKeySaveStatus('Failed to save API key');
    }
  };

  const handleResetSurvey = () => {
    if (showConfirmReset) {
      onResetSurvey();
      setShowConfirmReset(false);
      onClose();
    } else {
      setShowConfirmReset(true);
    }
  };

  const handleCancelReset = () => {
    setShowConfirmReset(false);
  };

  const handleExportData = async () => {
    if (!sessionId) {
      alert('No session data available to export');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/sessions/${sessionId}/export`);
      const data = await response.json();

      if (response.ok) {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `survey-export-${sessionId}-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        alert(`Export failed: ${data.error}`);
      }
    } catch (error) {
      alert(`Export failed: ${error.message}`);
    }
  };

  const handleDownloadUserResponse = async (userSessionId, username) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/sessions/${userSessionId}/export-excel`, {
        credentials: 'include'
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${username}-responses-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        const errorData = await response.json();
        alert(`Export failed: ${errorData.error}`);
      }
    } catch (error) {
      alert(`Export failed: ${error.message}`);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setIsUploading(true);
    setUploadStatus('Uploading...');

    try {
      const response = await fetch(`${API_URL}/api/upload-survey`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        setUploadStatus(`✓ Success! Uploaded ${result.stats.domains} domains, ${result.stats.subcategories} subcategories, ${result.stats.questions} questions`);
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setUploadStatus(`✗ Error: ${result.error}`);
      }
    } catch (error) {
      setUploadStatus(`✗ Error: ${error.message}`);
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="settings-overlay" onClick={onClose}></div>
      <div className="settings-panel">
        <div className="settings-header">
          <h2>Admin Settings</h2>
          <button className="settings-close" onClick={onClose}>×</button>
        </div>

        <div className="settings-content">
          <section className="settings-section">
            <h3>Application Settings</h3>
            <div className="settings-item">
              <label>Application Title:</label>
              <input
                type="text"
                className="title-input"
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
                placeholder="Engineering Maturity Assessment"
              />
              <button 
                className="settings-btn settings-btn-primary" 
                onClick={handleSaveTitle}
                style={{ marginTop: '10px' }}
              >
                <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                Save Title
              </button>
              {titleSaveStatus && (
                <div className="upload-status success" style={{ marginTop: '10px' }}>
                  {titleSaveStatus}
                </div>
              )}
              <p className="settings-description">
                Customize the application title shown in the header and browser tab
              </p>
            </div>

            <div className="settings-item">
              <label>Anthropic API Key:</label>
              {apiKeyConfigured && maskedApiKey && (
                <div className="api-key-status">
                  <span className="status-badge configured">✓ Configured</span>
                  <span className="masked-key">{maskedApiKey}</span>
                </div>
              )}
              <input
                type="password"
                className="title-input"
                value={anthropicApiKey}
                onChange={(e) => setAnthropicApiKey(e.target.value)}
                placeholder="sk-ant-..."
              />
              <button 
                className="settings-btn settings-btn-primary" 
                onClick={handleSaveApiKey}
                style={{ marginTop: '10px' }}
              >
                <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                Save API Key
              </button>
              {apiKeySaveStatus && (
                <div className={`upload-status ${apiKeySaveStatus.includes('✓') ? 'success' : 'error'}`}>
                  {apiKeySaveStatus}
                </div>
              )}
              <p className="settings-description">
                Enter your Anthropic API key to enable AI-powered question insights. Get your key from <a href="https://console.anthropic.com/" target="_blank" rel="noopener noreferrer">Anthropic Console</a>.
              </p>
            </div>
          </section>

          <section className="settings-section">
            <h3>AI Prompt Template</h3>
            <div className="settings-item">
              <label htmlFor="prompt-template">Custom Prompt Template</label>
              <textarea
                id="prompt-template"
                className="prompt-template-input"
                value={promptTemplate}
                onChange={(e) => setPromptTemplate(e.target.value)}
                placeholder="Enter your custom prompt template. Use {{domain}}, {{subcategory}}, and {{question}} as placeholders. Leave empty to use default template."
                rows="12"
              />
              <button 
                className="settings-btn"
                onClick={handleSavePromptTemplate}
                style={{ marginTop: '10px' }}
              >
                <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                Save Prompt Template
              </button>
              {promptSaveStatus === 'saving' && (
                <div className="upload-status">Saving...</div>
              )}
              {promptSaveStatus === 'success' && (
                <div className="upload-status success">✓ Prompt template saved successfully</div>
              )}
              {promptSaveStatus === 'error' && (
                <div className="upload-status error">Failed to save prompt template</div>
              )}
              <p className="settings-description">
                Customize the AI prompt template for question insights. Use placeholders: <code>{'{domain}'}</code>, <code>{'{subcategory}'}</code>, and <code>{'{question}'}</code> to insert dynamic values.
              </p>
            </div>
          </section>

          <section className="settings-section">
            <h3>Session Information</h3>
            <div className="settings-item">
              <label>Session ID:</label>
              <div className="session-id-display">{sessionId || 'Not available'}</div>
            </div>
          </section>

          <section className="settings-section">
            <h3>Survey Configuration</h3>
            <div className="settings-item">
              <label className="upload-label">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  disabled={isUploading}
                  style={{ display: 'none' }}
                />
                <button 
                  className="settings-btn settings-btn-primary" 
                  onClick={(e) => e.currentTarget.previousElementSibling.click()}
                  disabled={isUploading}
                >
                  <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  {isUploading ? 'Uploading...' : 'Upload Survey Structure'}
                </button>
              </label>
              <p className="settings-description">
                Upload an Excel file with 3 columns: Domain, Subcategory, Question
              </p>
              {uploadStatus && (
                <div className={`upload-status ${uploadStatus.startsWith('✓') ? 'success' : 'error'}`}>
                  {uploadStatus}
                </div>
              )}
            </div>
          </section>

          <section className="settings-section">
            <h3>Data Management</h3>
            <div className="settings-item">
              <button className="settings-btn settings-btn-secondary" onClick={handleExportData}>
                <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Export Survey Data
              </button>
              <p className="settings-description">Download your survey responses as JSON</p>
            </div>

            <div className="settings-item">
              {!showConfirmReset ? (
                <>
                  <button className="settings-btn settings-btn-danger" onClick={handleResetSurvey}>
                    <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="1 4 1 10 7 10" />
                      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                    </svg>
                    Reset Survey
                  </button>
                  <p className="settings-description">Clear all responses and start over</p>
                </>
              ) : (
                <div className="confirm-reset">
                  <p className="warning-text">⚠️ Are you sure? This will delete all your responses.</p>
                  <div className="confirm-buttons">
                    <button className="settings-btn settings-btn-danger" onClick={handleResetSurvey}>
                      Yes, Reset
                    </button>
                    <button className="settings-btn settings-btn-secondary" onClick={handleCancelReset}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="settings-section">
            <h3>User Response Downloads</h3>
            <div className="settings-item">
              <p className="settings-description">
                Download individual user responses as Excel files
              </p>
              {isLoadingSessions ? (
                <div className="loading-sessions">Loading user sessions...</div>
              ) : userSessions.length === 0 ? (
                <div className="no-sessions">
                  No user sessions found. Check the browser console for details.
                  <br />
                  <small>Make sure users have started the survey and the server is running.</small>
                </div>
              ) : (
                <div className="user-sessions-list">
                  {userSessions.map((session) => (
                    <div key={session.session_id} className="user-session-item">
                      <div className="session-info">
                        <div className="session-username">
                          <strong>{session.username}</strong>
                          {session.user_role === 'admin' && <span className="admin-badge-small">Admin</span>}
                        </div>
                        <div className="session-details">
                          <span className="session-completion">
                            {session.completion_percentage}% complete
                          </span>
                          <span className="session-date">
                            Last updated: {new Date(session.updated_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <button
                        className="settings-btn settings-btn-secondary download-btn-small"
                        onClick={() => handleDownloadUserResponse(session.session_id, session.username)}
                      >
                        <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="settings-section">
            <h3>About</h3>
            <div className="settings-item">
              <p className="about-text">
                <strong>Engineering Maturity Assessment</strong><br />
                Version 1.0.0<br />
                A comprehensive tool for evaluating engineering capabilities across multiple domains.
              </p>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

export default Settings;
